// The one place the frontend learns what the meetstandaard contains.
//
// TWO API pipelines are live at once, and that is not a week-long transition:
//   legacy    /database                          → arbeidsparticipatie, gelijke-kansen
//   versioned /meetstandaard/{sector}/{version}  → energiearmoede (the rest follow)
// energiearmoede will NEVER appear in /database (confirmed by the api team,
// inbox #96). Any consumer that reads only /database gets a clean 200 back with
// a whole sector missing — which is why every past failure here was silent.
//
// Consumers differ in what they need out of the catalogue (a picker list, a
// score map, an msiId→name index, a sector list), so this module returns the
// raw halves and lets each shape them. What it does NOT leave to the caller is
// *which* pipelines exist — copying a URL constant out of a neighbouring file
// is exactly how the blind consumers were created.
//
// AI-CONTEXT: See docs/pitfalls.md#api-dual-pipeline-legacy-only-consumer

import { PINNED_STANDAARD_VERSIONS, standaardDocumentUrl } from './standaardVersions'
import adaptMeetstandaardDocument from './adaptMeetstandaardDocument'

const DATABASE_URL = 'https://us-central1-meetstandaard-api.cloudfunctions.net/database'

// Module-level cache: several components mount catalogue consumers at once
// (EffectManager, AddEffectFlow, the dashboard cards), and the catalogue is
// static per session. A failed fetch clears its own cache so the next mount
// retries instead of being stuck until a hard refresh.
let cachedLegacyPromise = null
let cachedStandaardenPromise = null

const fetchJson = async (url, label) => {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`${label} gaf status ${response.status}`)
    return response.json()
}

const fetchLegacyCatalog = () => {
    if (!cachedLegacyPromise) {
        cachedLegacyPromise = fetchJson(DATABASE_URL, 'Meetstandaard-api').catch((error) => {
            cachedLegacyPromise = null
            throw error
        })
    }
    return cachedLegacyPromise
}

const fetchVersionedStandaarden = () => {
    if (!cachedStandaardenPromise) {
        cachedStandaardenPromise = Promise.all(
            Object.entries(PINNED_STANDAARD_VERSIONS).map(([sector, version]) =>
                fetchJson(standaardDocumentUrl(sector, version), `Meetstandaard ${sector} ${version}`)
                    .then(adaptMeetstandaardDocument)
            )
        ).catch((error) => {
            cachedStandaardenPromise = null
            throw error
        })
    }
    return cachedStandaardenPromise
}

/**
 * Both catalogues, or an error. There is deliberately no partial result:
 * falling back to legacy-only would re-create the bug in its worst form —
 * telling the user with confidence that an effect does not exist.
 *
 * @returns {Promise<{legacy: Array<object>, documents: Array<{sector: string, version: string, effects: Array<object>, skippedEffects: Array<object>}>}>}
 */
const fetchStandaardCatalogs = async () => {
    const [legacy, documents] = await Promise.all([
        fetchLegacyCatalog(),
        fetchVersionedStandaarden(),
    ])
    return { legacy, documents }
}

/** Test seam — the module cache would otherwise leak between test files. */
const __resetStandaardCatalogCache = () => {
    cachedLegacyPromise = null
    cachedStandaardenPromise = null
}

export default fetchStandaardCatalogs
export { DATABASE_URL, __resetStandaardCatalogCache }
